import { defineMessages, useIntl } from '@alltrails/shared/react-intl';
import Print from '@alltrails/shared/icons/Print';
import { COLOR_INTERACTIVE_DEFAULT } from '@alltrails/shared/denali/tokens';
import { TrailMoreAlternateMenuItem } from 'react_components/main_action_bar/trail_more_alternate_menu_item';
import { logTrailDetailsPrintClicked, logTrailDetailsShareClicked } from 'utils/trail_analytics_helpers';
import useUser from 'hooks/useUser';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import User from '@alltrails/shared/types/User';
import { Trail } from 'types/Trails';
import { Context } from '@alltrails/shared/types/context';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import Texture from '@alltrails/shared/denali/components/Texture';
import * as styles from './styles/styles.module.scss';
import ActionBarItem from '../ActionBarItem';
import PhotosMenuItem from '../TrailDetails/PhotosMenuItem';
import DirectionsButton from '../DirectionsButton';
import SharePopupButton from '../SharePopupButton/SharePopupButton';
import { LanguageSupportUtil } from '../../utils/language_support_util';

const TRAIL_ACTION_STRINGS = defineMessages({
  PRINT_PDF_MAP: { defaultMessage: 'Print/PDF map' }
});

function getPrintUrl(languageRegionCode: LanguageRegionCode, user: User, trail: Trail) {
  const url = LanguageSupportUtil.wrapUrlSafe(`/${trail.slug}/print`, languageRegionCode);

  if (user?.pro) {
    return url;
  }

  const params = new URLSearchParams();

  params.set('returnTo', url);

  return LanguageSupportUtil.wrapUrlSafe(`/plus?${params.toString()}`, languageRegionCode);
}

type Props = {
  context: Context;
  exploreFlag?: unknown;
  pending?: boolean;
  photos?: unknown;
  numPhotos?: number;
  shareObject?: unknown;
  trail?: Trail;
};

const TrailActionBar = ({ context, photos, numPhotos, trail, pending, exploreFlag, shareObject }: Props) => {
  const {
    formattedDefaultMessages: { PRINT_PDF_MAP },
    intl
  } = useFormatMessage(TRAIL_ACTION_STRINGS);

  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const printUrl = getPrintUrl(languageRegionCode, user, trail);

  return (
    <Texture className={styles.mainActionBar} variant="rock" imageSize="cropped">
      <ul className={styles.list}>
        <PhotosMenuItem
          adminUser={context.adminUser}
          photos={photos}
          count={numPhotos}
          trailId={trail.trail_id}
          slug={trail.slug}
          languageRegionCode={languageRegionCode}
          intl={intl}
        />
        <DirectionsButton disabled={!trail._geoloc || !trail._geoloc.lat || !trail._geoloc.lng} geoloc={trail._geoloc} trailId={trail.trail_id} />
        {(!context.mobileBrowser || !exploreFlag) && (
          <ActionBarItem
            icon={<Print color={COLOR_INTERACTIVE_DEFAULT} />}
            disabled={pending}
            title={PRINT_PDF_MAP}
            className={styles.mobileHide}
            testId="print-action"
            onClick={() => {
              logTrailDetailsPrintClicked(trail.trail_id);
              window.location.assign(printUrl);
            }}
            showPlus
          />
        )}
        <SharePopupButton
          className={styles.mobileHide}
          shareObject={shareObject}
          trail={trail}
          onClick={() => {
            if (trail) logTrailDetailsShareClicked(trail.trail_id);
          }}
        />
        <TrailMoreAlternateMenuItem
          mobileBrowser={context.mobileBrowser}
          trail={trail}
          pending={pending}
          printUrl={printUrl}
          exploreFlag={exploreFlag}
          intl={intl}
        />
      </ul>
    </Texture>
  );
};

TrailActionBar.defaultProps = {
  exploreFlag: false,
  pending: false,
  shareObject: {},
  photos: [],
  numPhotos: null
};

export default TrailActionBar;
